import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { BLUE, BORDER, INTER, NAVY, SLATE, SURFACE_TINT } from '../../style/verbaScholaStyles';

export interface PdfExcerpt {
    filename: string;
    pages: string;
    text: string;
}

function validPageRange(value: string): boolean {
    if (!/^\s*\d+(?:\s*-\s*\d+)?(?:\s*,\s*\d+(?:\s*-\s*\d+)?)*\s*$/.test(value)) return false;
    return value.split(',').every(part => {
        const [first, last = first] = part.trim().split(/\s*-\s*/).map(Number);
        return first >= 1 && last >= first;
    });
}

export default function PdfPagePicker({ destination, onUse }: { destination: string; onUse: (excerpt: PdfExcerpt) => void }) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [file, setFile] = React.useState<File | null>(null);
    const [pdfUrl, setPdfUrl] = React.useState('');
    const [pages, setPages] = React.useState('');
    const [reviewText, setReviewText] = React.useState('');
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [fileError, setFileError] = React.useState('');
    const [added, setAdded] = React.useState(false);
    const pagesValid = validPageRange(pages);
    const firstPage = Number(pages.match(/^\s*(\d+)/)?.[1] ?? 1);

    React.useEffect(() => {
        if (!file) { setPdfUrl(''); return; }
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const chooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0];
        if (!selected) return;
        event.target.value = '';
        setPages('');
        setReviewText('');
        setPreviewOpen(false);
        setAdded(false);
        if (!selected.name.toLowerCase().endsWith('.pdf')) {
            setFile(null);
            setFileError('Choose a PDF file.');
            return;
        }
        setFile(selected);
        setFileError('');
    };

    return <Box sx={{ p: { xs: 2, sm: 2.5 }, border: `1px solid ${BORDER}`, borderRadius: '14px', backgroundColor: SURFACE_TINT }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
            <PictureAsPdfOutlinedIcon sx={{ color: BLUE, mt: 0.25 }} />
            <Box>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 14 }}>Select PDF pages for {destination}</Typography>
                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mt: 0.25 }}>Use the PDF page numbers, which may differ from the page numbers printed in the book.</Typography>
            </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <input ref={inputRef} type="file" accept=".pdf,application/pdf" onChange={chooseFile} aria-label={`${destination} PDF`} style={{ display: 'none' }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" startIcon={<UploadFileRoundedIcon />} onClick={() => inputRef.current?.click()} sx={{ minHeight: 44, fontFamily: INTER, fontWeight: 700, textTransform: 'none', borderColor: BORDER, color: BLUE, backgroundColor: '#fff' }}>
                        {file ? 'Change PDF' : 'Choose PDF'}
                    </Button>
                    {file && <Button onClick={() => setPreviewOpen(open => !open)} aria-expanded={previewOpen} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none', color: BLUE }}>
                        {previewOpen ? 'Hide PDF' : 'Preview PDF'}
                    </Button>}
                </Stack>
                <Typography sx={{ fontFamily: INTER, color: file ? NAVY : SLATE, fontSize: 12, mt: 0.75, overflowWrap: 'anywhere' }}>{file?.name ?? 'No file chosen'}</Typography>
                {fileError && <Typography role="alert" sx={{ fontFamily: INTER, color: '#B42335', fontSize: 12, mt: 0.5 }}>{fileError}</Typography>}
            </Box>
            <TextField
                label="Pages to include"
                value={pages}
                onChange={event => { setPages(event.target.value); setAdded(false); }}
                placeholder="e.g. 12-18, 23"
                size="small"
                error={Boolean(pages.trim()) && !pagesValid}
                helperText={pages.trim() && !pagesValid ? 'Enter page numbers or ranges such as 12-18, 23.' : 'You can include more than one range.'}
                inputProps={{ 'aria-label': `${destination} pages to include` }}
                sx={{ flex: 1, minWidth: { sm: 225 }, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' }, '& .MuiFormHelperText-root': { fontFamily: INTER } }}
            />
        </Stack>

        {previewOpen && pdfUrl && <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, mb: 0.75 }}>Preview opens at the first selected PDF page.</Typography>
            <Box component="iframe" title={`${destination} PDF preview`} src={`${pdfUrl}#page=${firstPage}`} sx={{ width: '100%', height: { xs: 330, md: 450 }, border: `1px solid ${BORDER}`, borderRadius: '10px', backgroundColor: '#fff' }} />
        </Box>}

        <Alert severity="info" sx={{ mt: 2, fontFamily: INTER, fontSize: 12 }}>
            This mockup has no OCR connection. In the finished flow, text from the selected pages appears below for review. Paste sample text here to try the next step.
        </Alert>
        <TextField
            label="Review extracted text"
            value={reviewText}
            onChange={event => { setReviewText(event.target.value); setAdded(false); }}
            placeholder="Text from the selected pages will appear here. Correct characters, pinyin, and reading order before using it."
            multiline minRows={4} fullWidth
            inputProps={{ 'aria-label': `${destination} reviewed PDF text` }}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { alignItems: 'flex-start', backgroundColor: '#fff' } }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }}>
            <Button variant="contained" disabled={!file || !pagesValid || !reviewText.trim()} onClick={() => {
                if (!file) return;
                onUse({ filename: file.name, pages: pages.replace(/\s+/g, ''), text: reviewText.trim() });
                setAdded(true);
            }} sx={{ minHeight: 44, fontFamily: INTER, fontWeight: 700, textTransform: 'none', backgroundColor: BLUE, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                {destination === 'lesson brief' ? 'Add pages to lesson brief' : 'Append pages to pedagogy'}
            </Button>
            {added && <Typography role="status" sx={{ fontFamily: INTER, fontSize: 12, color: '#24724F' }}>Added. You can edit the combined text below.</Typography>}
        </Stack>
    </Box>;
}
